import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Table, Tag, message } from 'antd';
import { BellIcon } from '@heroicons/react/24/outline';
import { Navigate } from 'react-router-dom';
import AppContext from '../contexts/AppContext';
import usePushEnabled from '../utils/usePushEnabled';
import { notificationRequest as request } from '../utils/notificationRequest';
import { readPendingNotificationRequests, savePendingNotificationRequests, pendingNotificationTitle, notificationFingerprint, matchesNotificationFingerprint } from '../utils/pendingNotificationRequests';

const TITLE_MAX = 50;
const BODY_MAX = 140;

/** What the guest will see on their phone, as close as we can show it. */
const Preview = ({ venueName, title, body }) => (
  <div className="w-full max-w-sm rounded-2xl bg-gray-100 p-3 shadow-inner" aria-label="Notification preview">
    <div className="flex items-start gap-3 rounded-xl bg-white p-3 shadow">
      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gray-900 text-white">
        <BellIcon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs text-gray-500">{venueName || 'Your venue'} · now</p>
        <p className="truncate text-sm font-semibold text-gray-900">{title || 'Title of your message'}</p>
        <p className="text-sm text-gray-700">{body || 'The message guests will see on their lock screen.'}</p>
      </div>
    </div>
  </div>
);

const VenueNotifications = () => {
  const { token, selectedRole, userInfo } = useContext(AppContext);
  const venueId = selectedRole?.type === 'venue' ? selectedRole.id : null;

  const venueName = useMemo(() => {
    const list = userInfo?.roleInfo?.venue || [];
    return list.find((v) => v.id === venueId)?.name || '';
  }, [userInfo, venueId]);

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState('');
  const [pendingRequests, setPendingRequests] = useState(() => readPendingNotificationRequests(venueId));
  const [checkingRequestId, setCheckingRequestId] = useState(null);
  const checkingStatus = Boolean(checkingRequestId);
  const [statusNotice, setStatusNotice] = useState(null);
  const active = useRef(true);
  const modal = useRef(null);
  const confirming = useRef(false);
  const sendInFlight = useRef(false);
  const loadVersion = useRef({ value: 0 });
  const pendingRequestsRef = useRef(pendingRequests);
  const draftRequestRef = useRef(null);
  const statusInFlight = useRef(false);
  const draftRef = useRef({ title, body, url });

  useEffect(() => { draftRef.current = { title, body, url }; }, [title, body, url]);

  useEffect(() => {
    active.current = true;
    const counter = loadVersion.current;
    return () => {
      active.current = false;
      counter.value++;
      modal.current?.destroy();
    };
  }, []);

  const load = useCallback(async () => {
    if (!venueId || !token) return;
    const version = ++loadVersion.current.value;
    setLoading(true);
    setLoadError('');
    try {
      const data = await request(`/private/venue/${encodeURIComponent(venueId)}/notifications`, token);
      if (active.current && version === loadVersion.current.value) setOverview(data);
    } catch (e) {
      if (active.current && version === loadVersion.current.value) setLoadError(e.message || 'Failed to load notifications.');
    } finally {
      if (active.current && version === loadVersion.current.value) setLoading(false);
    }
  }, [venueId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const updatePendingRequests = (requests) => {
    pendingRequestsRef.current = requests;
    setPendingRequests(requests);
    savePendingNotificationRequests(venueId, requests);
  };

  const clearPendingRequest = (requestId) => {
    if (draftRequestRef.current?.requestId === requestId) draftRequestRef.current = null;
    updatePendingRequests(pendingRequestsRef.current.filter(entry => entry.requestId !== requestId));
  };

  const clearDraftIfUnchanged = (fingerprint) => {
    const draft = draftRef.current;
    if (matchesNotificationFingerprint(fingerprint, notificationFingerprint(draft.title, draft.body, draft.url))) {
      setTitle(''); setBody(''); setUrl('');
    }
  };

  const checkSendingStatus = async (saved) => {
    if (!pendingRequestsRef.current.some(entry => entry.requestId === saved.requestId)
      || !token || statusInFlight.current || sendInFlight.current || confirming.current) return;
    statusInFlight.current = true;
    setCheckingRequestId(saved.requestId);
    setStatusNotice(null);
    try {
      const result = await request(`/private/venue/${encodeURIComponent(venueId)}/notifications/requests/${encodeURIComponent(saved.requestId)}`, token);
      if (!active.current) return;
      if (result.deliveryStatus === 'unknown') {
        setStatusNotice({ type: 'info', message: 'The delivery outcome is still unconfirmed. Checking status does not send another notification.' });
      } else {
        setStatusNotice({ type: 'success', message: `Push service accepted ${result.delivered} of ${result.recipients} requests. Phone delivery is not confirmed.` });
        clearPendingRequest(saved.requestId);
        // Keep a newer draft intact when checking an older request.
        clearDraftIfUnchanged(saved.fingerprint);
      }
      await load();
    } catch (error) {
      if (active.current) setStatusNotice({ type: 'error', message: error.message || 'Could not check notification status. Please try again.' });
    } finally {
      statusInFlight.current = false;
      if (active.current) setCheckingRequestId(null);
    }
  };

  if (!venueId) {
    return (
      <div className="p-6">
        <Alert type="info" showIcon message="Notifications are sent by a venue. Please select a venue account." />
      </div>
    );
  }

  const titleOk = title.trim().length > 0 && [...title].length <= TITLE_MAX;
  const bodyOk = body.trim().length > 0 && [...body].length <= BODY_MAX;
  const canSend = Boolean(overview?.configured) && titleOk && bodyOk && !sending && !checkingStatus && !loading && !loadError;

  const confirmAndSend = () => {
    if (!canSend || confirming.current || sendInFlight.current || statusInFlight.current) return;
    confirming.current = true;
    setFormError('');
    const fingerprint = JSON.stringify([title, body, url.trim()]);
    const normalized = notificationFingerprint(title, body, url);
    // Always recover the original ID, even after sending an unrelated message.
    let previous = pendingRequestsRef.current.find(entry => matchesNotificationFingerprint(entry.fingerprint, normalized));
    if (!previous && draftRequestRef.current && matchesNotificationFingerprint(draftRequestRef.current.fingerprint, normalized)) previous = draftRequestRef.current;
    if (!previous) {
      const bytes = new Uint8Array(16);
      if (!window.crypto?.getRandomValues) {
        confirming.current = false;
        setFormError('Your browser cannot safely create a notification request. Please use a current browser over HTTPS.');
        return;
      }
      window.crypto.getRandomValues(bytes);
      previous = { fingerprint, requestId: Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('') };
    }
    draftRequestRef.current = previous;
    const requestId = previous.requestId;
    const count = overview?.subscribers ?? 0;
    modal.current = Modal.confirm({
      title: 'Send this notification?',
      content: (
        <div>
          <p className="mb-2">
            It will be sent to <strong>{count}</strong> device{count === 1 ? '' : 's'} that turned on notifications for{' '}
            {venueName || 'this venue'}. It cannot be recalled.
          </p>
          <p className="font-semibold">{title}</p>
          <p>{body}</p>
        </div>
      ),
      okText: 'Send now',
      cancelText: 'Cancel',
      afterClose: () => { confirming.current = false; modal.current = null; },
      onOk: async () => {
        if (!active.current || sendInFlight.current || statusInFlight.current) return;
        sendInFlight.current = true;
        setSending(true);
        if (!pendingRequestsRef.current.some(entry => entry.requestId === requestId)) {
          updatePendingRequests([...pendingRequestsRef.current, previous]);
        }
        setStatusNotice(null);
        try {
          const result = await request(`/private/venue/${encodeURIComponent(venueId)}/notifications`, token, {
            method: 'POST',
            body: JSON.stringify({ title, body, url: url.trim() || undefined, requestId }),
          });
          if (!active.current) return;
          if (result.deliveryStatus === 'unknown') {
            message.warning('This request was already reserved. Its delivery outcome is unconfirmed; retrying this request will not resend it.');
          } else {
            const notify = result.failed || result.removed ? message.warning : message.success;
            notify(result.recipients === 0
              ? 'No subscribers. No notifications were sent.'
              : `Push service accepted ${result.delivered} of ${result.recipients} requests. Phone delivery is not confirmed.`);
            clearPendingRequest(requestId);
            clearDraftIfUnchanged(fingerprint);
          }
          await load();
        } catch (e) {
          if (active.current) setFormError((e.message || 'Could not confirm the send.') + ' Retry the same message to check it safely.');
        } finally {
          sendInFlight.current = false;
          if (active.current) setSending(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Sent',
      dataIndex: 'createdAt',
      width: 170,
      render: (v) => new Date(v).toLocaleString(),
    },
    {
      title: 'Message',
      render: (_, r) => (
        <div>
          <div className="font-semibold">{r.title}</div>
          <div className="text-gray-600">{r.body}</div>
          {r.url ? <div className="text-xs text-gray-400">{r.url}</div> : null}
        </div>
      ),
    },
    {
      title: 'Accepted by push service',
      width: 140,
      render: (_, r) => (
        <span>
          {r.deliveryStatus === 'unknown' ? 'Outcome unconfirmed' : `${r.delivered} / ${r.recipients}`}
          {r.failed ? <Tag color="orange" className="ml-2">{r.failed} failed</Tag> : null}
          {r.uncertain ? <Tag color="orange" className="ml-2">{r.uncertain} unconfirmed</Tag> : null}
          {r.removed ? <Tag className="ml-2">{r.removed} expired</Tag> : null}
        </span>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 pb-16">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        <p className="text-gray-600">
          Send a short message to guests who turned on notifications for {venueName || 'your venue'}. It appears on
          their phone like an app notification, even if the page is closed.
        </p>
      </div>

      {loadError && <Alert type="error" showIcon message={loadError} />}
      {pendingRequests.length > 0 && (
        <div className="space-y-2 rounded-xl bg-white p-4 shadow">
          <p className="text-sm text-gray-600">Previous sends can be checked without sending them again.</p>
          <ul className="space-y-3">
            {pendingRequests.map(saved => (
              <li key={saved.requestId} data-testid={`pending-request-${saved.requestId}`} className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{pendingNotificationTitle(saved)}</span>
                <Button onClick={() => checkSendingStatus(saved)} loading={checkingRequestId === saved.requestId}
                  disabled={!token || sending || checkingStatus} aria-label={`Check sending status: ${pendingNotificationTitle(saved)}`}>
                  Check sending status
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {statusNotice && <Alert type={statusNotice.type} showIcon message={statusNotice.message} />}
      {overview && !overview.configured && (
        <Alert
          type="warning"
          showIcon
          message="Notifications are not set up on the server yet"
          description="Please contact Platypus360. You can prepare a message below, but it cannot be sent yet."
        />
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-xl bg-white p-4 shadow">
          <p className="text-sm text-gray-500">Devices subscribed</p>
          <p className="text-3xl font-semibold" data-testid="subscribers">{loading ? '…' : overview?.subscribers ?? 0}</p>
        </div>
        <div className="rounded-xl bg-white p-4 shadow">
          <p className="text-sm text-gray-500">Sent in the last 24 hours</p>
          <p className="text-3xl font-semibold" data-testid="sent-today">
            {loading ? '…' : overview?.sentToday ?? 0}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 rounded-xl bg-white p-5 shadow lg:grid-cols-2">
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="notification-title">Title</label>
            <Input
              id="notification-title"
              value={title}
              maxLength={TITLE_MAX}
              showCount
              placeholder="e.g. Pool closed this afternoon"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="notification-body">Message</label>
            <Input.TextArea
              id="notification-body"
              value={body}
              maxLength={BODY_MAX}
              showCount
              rows={3}
              placeholder="Keep it short: only the first lines show on a lock screen."
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="notification-url">
              Opens (optional)
            </label>
            <Input
              id="notification-url"
              value={url}
              placeholder="/service  or  https://…"
              onChange={(e) => setUrl(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              A page in your compendium (starting with /), or a full https address. Leave empty to open the home page.
            </p>
          </div>
          {formError && <Alert type="error" showIcon message={formError} />}
          <Button type="primary" size="large" disabled={!canSend} loading={sending} onClick={confirmAndSend}>
            Send notification
          </Button>
        </div>
        <div>
          <p className="mb-2 text-sm font-medium text-gray-700">Preview</p>
          <Preview venueName={venueName} title={title.trim()} body={body.trim()} />
        </div>
      </div>

      <div className="rounded-xl bg-white p-5 shadow">
        <h2 className="mb-3 text-lg font-semibold">Recent notifications</h2>
        <p className="mb-3 text-sm text-gray-500">Accepted requests do not confirm that a phone displayed the notification or that a guest read it.</p>
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={overview?.history || []}
          pagination={false}
          locale={{ emptyText: 'Nothing sent yet.' }}
        />
      </div>
    </div>
  );
};

// Remount on account or venue changes so old drafts, responses and modals cannot leak across roles.
const Notifications = () => {
  const { token, selectedRole } = useContext(AppContext);
  const pushEnabled = usePushEnabled(selectedRole?.id, selectedRole?.type);
  // A venue without notifications has no such page, even when its address is typed in by hand.
  if (selectedRole?.type === 'venue' && pushEnabled === false) return <Navigate to="/" replace />;
  return <VenueNotifications key={JSON.stringify([token, selectedRole?.type, selectedRole?.id])} />;
};

export default Notifications;
